import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { manageServer } from '../api/admin';
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, TextField } from '@mui/material';

interface MachineActionsManagerProps {
    server: any;
    action: 'block' | 'unblock' | 'deactivate' | 'replace' | 'reactivate' | null;
    onClose: () => void;
    onSuccess: (message: string) => void;
    onError: (error: string) => void;
}

export function MachineActionsManager({ server, action, onClose, onSuccess, onError }: MachineActionsManagerProps) {
    const queryClient = useQueryClient();
    const [blockReason, setBlockReason] = useState('');

    const actionMutation = useMutation({
        mutationFn: ({ serverId, actionName, blockedReason }: { serverId: string; actionName: string; blockedReason?: string }) => 
            manageServer(serverId, actionName, blockedReason),
        onSuccess: (_, variables) => {
            // Invalidate queries to refresh dashboard/machines lists/timeline logs
            queryClient.invalidateQueries({ queryKey: ['servers'] });
            queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
            queryClient.invalidateQueries({ queryKey: ['licenses'] });
            queryClient.invalidateQueries({ queryKey: ['logs'] });
            queryClient.invalidateQueries({ queryKey: ['customers'] });
            queryClient.invalidateQueries({ queryKey: ['network-insights'] });

            const act = variables.actionName;
            let successMessage = `Machine ${act}ed successfully`;
            if (act === 'replace') {
                successMessage = 'Machine migrated successfully';
            } else if (act === 'reactivate') {
                successMessage = 'Machine reactivated successfully';
            }
            onSuccess(successMessage);
            onClose();
        },
        onError: (err: any) => {
            const rawError = err.response?.data?.error;
            let friendlyMessage = err.response?.data?.message || err.message || 'Operation failed';
            if (rawError === 'machine_limit_exceeded') {
                friendlyMessage = 'Machine limit exceeded for this license';
            }
            onError(friendlyMessage);
            onClose();
        }
    });

    useEffect(() => {
        if (!server || !action) return;

        // Reset block reason input when the server or action changes
        if (action === 'block') {
            setBlockReason('');
        }

        // Direct execution for actions that do not require verification dialogs
        if (action === 'unblock' || action === 'deactivate') {
            actionMutation.mutate({ serverId: server.serverId, actionName: action });
        }
    }, [server, action]);

    if (!server || !action) return null;

    const handleConfirmBlock = () => {
        actionMutation.mutate({
            serverId: server.serverId,
            actionName: 'block',
            blockedReason: blockReason || undefined
        });
    };

    const handleConfirmReplace = () => {
        actionMutation.mutate({
            serverId: server.serverId,
            actionName: 'replace'
        });
    };

    const handleConfirmReactivate = () => {
        actionMutation.mutate({
            serverId: server.serverId,
            actionName: 'reactivate'
        });
    };

    return (
        <>
            {/* Replace Machine Confirmation Dialog */}
            <Dialog
                open={action === 'replace'}
                onClose={onClose}
                aria-labelledby="replace-dialog-title"
                aria-describedby="replace-dialog-description"
            >
                <DialogTitle id="replace-dialog-title">
                    {"Replace Machine?"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="replace-dialog-description">
                        Are you sure you want to replace this machine? This machine will stop validating and free one license slot.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={actionMutation.isPending}>Cancel</Button>
                    <Button 
                        onClick={handleConfirmReplace} 
                        color="warning" 
                        autoFocus
                        disabled={actionMutation.isPending}
                    >
                        Replace Machine
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Block Machine Input Dialog */}
            <Dialog
                open={action === 'block'}
                onClose={onClose}
                aria-labelledby="block-dialog-title"
            >
                <DialogTitle id="block-dialog-title">{"Block Machine"}</DialogTitle>
                <DialogContent>
                    <DialogContentText className="!mb-4">
                        Please provide a reason for blocking this machine.
                    </DialogContentText>
                    <TextField
                        autoFocus
                        margin="dense"
                        id="reason"
                        label="Blocked Reason"
                        type="text"
                        fullWidth
                        variant="standard"
                        value={blockReason}
                        onChange={(e) => setBlockReason(e.target.value)}
                        disabled={actionMutation.isPending}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={actionMutation.isPending}>Cancel</Button>
                    <Button 
                        onClick={handleConfirmBlock} 
                        color="error"
                        disabled={actionMutation.isPending}
                    >
                        Block Machine
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Reactivate Machine Confirmation Dialog */}
            <Dialog
                open={action === 'reactivate'}
                onClose={onClose}
                aria-labelledby="reactivate-dialog-title"
                aria-describedby="reactivate-dialog-description"
            >
                <DialogTitle id="reactivate-dialog-title">
                    {"Re-activate Machine"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="reactivate-dialog-description">
                        Are you sure you want to re-activate this machine?<br />
                        This machine will start validating again and consume one machine slot from this license.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={actionMutation.isPending}>Cancel</Button>
                    <Button 
                        onClick={handleConfirmReactivate} 
                        color="success" 
                        autoFocus
                        disabled={actionMutation.isPending}
                    >
                        Re-activate
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
